import { useQuery } from "@apollo/client";
import { Autocomplete, Box, CircularProgress, TextField } from "@mui/material";
import { GET_USERS_QUERY } from "components/Tasks/userQueries";
import { getFullName } from "encamp-shared/src/utils/name";
import { AssigneePickerFragment } from "generated-graphql/graphql";
import { useDebounce } from "hooks/useDebounce";
import { uniqBy } from "lodash-es";
import { useMemo, useState } from "react";

type UserMultiSelectProps = {
  label: string;
  value: string[];
  onChange: (userIds: string[]) => void;
};

const USERS_SORT = [{ field: "name", sort: "asc" }];
const userLabel = (user: AssigneePickerFragment) => getFullName(user.person) || user.email;

/** Picks users by id, showing the chosen ones as chips. */
export function UserMultiSelect({ label, value, onChange }: UserMultiSelectProps) {
  const [inputValue, setInputValue] = useState("");
  const [search] = useDebounce(inputValue, 300);
  // Retain selected labels while the ID lookup refreshes and search results change.
  const [chosenUsers, setChosenUsers] = useState<AssigneePickerFragment[]>([]);

  // Saved filters only hold ids, so look up the selected users to label their chips.
  const { data: selectedData } = useQuery(GET_USERS_QUERY, {
    variables: {
      search: `id:${value.join("|")}`,
      page: 0,
      pageSize: value.length,
      sort: USERS_SORT,
    },
    skip: value.length === 0,
  });
  const { data, previousData, loading } = useQuery(GET_USERS_QUERY, {
    variables: { search, page: 0, pageSize: 20, sort: USERS_SORT },
  });
  const optionsData = data ?? previousData;
  const searching = inputValue !== search || loading;

  const selectedUsers = useMemo(() => {
    const known = uniqBy(
      [...(selectedData?.users.items ?? []), ...chosenUsers, ...(optionsData?.users.items ?? [])],
      "id"
    );
    return value.flatMap((id) => known.find((user) => user.id === id) ?? []);
  }, [value, selectedData, chosenUsers, optionsData]);

  return (
    <Autocomplete
      multiple
      size="small"
      options={optionsData?.users.items ?? []}
      value={selectedUsers}
      loading={searching}
      filterOptions={(options) => options}
      getOptionLabel={userLabel}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      inputValue={inputValue}
      onInputChange={(_, next, reason) => {
        if (reason !== "reset") {
          setInputValue(next);
        }
      }}
      onChange={(_, users) => {
        setChosenUsers(users);
        // Keep ids whose users haven't loaded yet; they aren't shown, so can't have been removed.
        const unresolved = value.filter((id) => !selectedUsers.some((user) => user.id === id));
        onChange([...unresolved, ...users.map((user) => user.id)]);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={value.length === 0 ? "Anyone" : ""}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                <Box component="span" sx={{ display: "inline-flex", width: 16, flexShrink: 0 }}>
                  {searching && <CircularProgress size={16} aria-label={`Searching ${label}`} />}
                </Box>
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
